import { supabase } from '@/lib/supabase';
import type { AgreementMessage as DbAgreementMessage } from '@/lib/database.types';

/** sender_id είναι nullable στη βάση (ο συμβαλλόμενος μπορεί να έχει διαγράψει
 * τον λογαριασμό του — GDPR right-to-erasure, βλ. migration
 * fix_account_deletion_fk_blockers) αλλά η υπόλοιπη εφαρμογή αναφέρεται πάντα
 * σε ένα σταθερό string id· ένα διαγραμμένο sender γίνεται DELETED_SENDER_ID,
 * που κανένα πραγματικό προφίλ δεν έχει, οπότε τα lookups απλά δεν βρίσκουν
 * τίποτα και δείχνουν το ήδη υπάρχον fallback ("Άγνωστος χρήστης"). */
export const DELETED_SENDER_ID = 'deleted-user';

export type AgreementMessage = Omit<DbAgreementMessage, 'sender_id'> & { sender_id: string };

export type { DbAgreementMessage };

const SELECT_COLUMNS = 'id, agreement_id, sender_id, body, created_at, reply_to_id';

function normalize(row: DbAgreementMessage): AgreementMessage {
  return { ...row, sender_id: row.sender_id ?? DELETED_SENDER_ID };
}

export async function fetchMessages(agreementId: string): Promise<AgreementMessage[]> {
  const { data, error } = await supabase
    .from('agreement_messages')
    .select(SELECT_COLUMNS)
    .eq('agreement_id', agreementId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as DbAgreementMessage[]).map(normalize);
}

export async function fetchLastMessage(agreementId: string): Promise<AgreementMessage | null> {
  const { data, error } = await supabase
    .from('agreement_messages')
    .select(SELECT_COLUMNS)
    .eq('agreement_id', agreementId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? normalize(data as DbAgreementMessage) : null;
}

/** Σημειώνει ως διαβασμένα τα μηνύματα αυτής της συμφωνίας για τον τρέχοντα
 * χρήστη — server-side (agreement_message_reads), ώστε το εικονίδιο
 * αδιάβαστου chat στην αρχική να δουλεύει σε όλες τις συσκευές του. */
export async function markAgreementMessagesRead(agreementId: string): Promise<void> {
  const { error } = await (supabase.rpc as any)('mark_agreement_messages_read', { p_agreement_id: agreementId });
  if (error) throw error;
}

/** Πότε διάβασε ο καθένας (creator+counterparty/parties) αυτό το chat —
 * user_id → last_read_at ISO string. Χρησιμοποιείται για την ένδειξη
 * "διαβάστηκε" δίπλα στα δικά μου μηνύματα (μήνυμα.created_at <= last_read_at
 * του άλλου). */
export async function fetchAgreementMessageReads(agreementId: string): Promise<Record<string, string>> {
  const { data, error } = await (supabase as any)
    .from('agreement_message_reads')
    .select('user_id, last_read_at')
    .eq('agreement_id', agreementId);
  if (error) throw error;
  const map: Record<string, string> = {};
  for (const row of (data ?? []) as { user_id: string; last_read_at: string }[]) map[row.user_id] = row.last_read_at;
  return map;
}

export type ChatPreview = {
  agreement_id: string;
  agreement_title: string;
  last_message_body: string;
  last_message_sender_id: string | null;
  last_message_created_at: string;
  unread: boolean;
};

/** Το πιο πρόσφατο μήνυμα ανά συμφωνία — για το ιστορικό chat στην αρχική
 * (ίδια λογική με το κουδούνι ειδοποιήσεων, αλλά για μηνύματα). */
export async function listMyChatPreviews(): Promise<ChatPreview[]> {
  const { data, error } = await (supabase.rpc as any)('list_my_chat_previews');
  if (error) throw error;
  return (data ?? []) as ChatPreview[];
}

export async function sendMessage(agreementId: string, senderId: string, body: string, replyToId?: string | null): Promise<AgreementMessage> {
  const trimmed = body.trim();
  const { data, error } = await supabase
    .from('agreement_messages')
    .insert({ agreement_id: agreementId, sender_id: senderId, body: trimmed, reply_to_id: replyToId ?? null })
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return normalize(data as DbAgreementMessage);
}

/** Επιτρεπόμενα emoji αντίδρασης σε μήνυμα chat — σταθερή λίστα, όχι ελεύθερη επιλογή. */
export const MESSAGE_REACTION_EMOJIS = ['👍', '👏', '✌️', '🤝', '😃'] as const;
export type MessageReactionEmoji = (typeof MESSAGE_REACTION_EMOJIS)[number];

export type MessageReaction = {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
};

export async function fetchReactions(agreementId: string): Promise<MessageReaction[]> {
  const { data, error } = await supabase
    .from('agreement_message_reactions')
    .select('id, message_id, user_id, emoji')
    .eq('agreement_id', agreementId);
  if (error) throw error;
  return (data ?? []) as MessageReaction[];
}

/**
 * Ένα reaction ανά χρήστη ανά μήνυμα — αν ξαναπατήσει το ΙΔΙΟ emoji το
 * αφαιρεί (toggle), αν πατήσει άλλο το αντικαθιστά (upsert). Ίδια λογική με
 * Facebook Messenger.
 */
export async function toggleReaction(
  agreementId: string,
  messageId: string,
  userId: string,
  emoji: MessageReactionEmoji,
  existing: MessageReaction | undefined,
): Promise<void> {
  if (existing && existing.emoji === emoji) {
    const { error } = await supabase.from('agreement_message_reactions').delete().eq('id', existing.id);
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from('agreement_message_reactions')
    .upsert({ agreement_id: agreementId, message_id: messageId, user_id: userId, emoji }, { onConflict: 'message_id,user_id' });
  if (error) throw error;
}
